<template>
  <div>
    <v-card flat>
      <v-card-text>
        <v-row dense>
          <v-col
            v-for="job in items"
            :key="job.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex align-items-stretch"
          >
            <v-card variant="flat" class="my-4 flex-grow-1 app-content-card">
              <v-card-text class="text-left">
                <h3>{{ $t("candidateTabs.experience.jobTitle") }}: {{ job.Job_title }}</h3>
                <v-divider></v-divider>
                <p><strong>{{ $t("candidateDetails.card.id") }}:</strong> {{ job.id }}</p>
                <p>
                  <strong>{{ $t("candidateTabs.experience.responsibility") }}:</strong>
                  {{ job.Responsibility }}
                </p>
                <p><strong>{{ $t("candidateTabs.experience.company") }}:</strong> {{ job.Company }}</p>
                <p>
                  <strong>{{ $t("candidateTabs.experience.start") }}:</strong>
                  {{ formatDate(job.Date_start) }}
                </p>
                <p>
                  <strong>{{ $t("candidateTabs.experience.finish") }}:</strong>
                  {{ job.Date_finish ? formatDate(job.Date_finish) : $t("candidateTabs.experience.inProgress") }}
                </p>
              </v-card-text>
              <v-card-actions>
                <v-btn icon @click="openEditJobDialog(job)">
                  <v-icon color="primary">mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="confirmDeleteJob(job.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
        <v-btn
          color="primary"
          @click="openAddJobDialog"
          class="mb-4 d-flex align-items-stretch"
        >
          {{ $t("candidateTabs.experience.addButton") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-dialog v-model="addJobDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.experience.addTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="addJob">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.jobTitle')} *`" v-model="newJob.Job_title" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.company')} *`" v-model="newJob.Company" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea :label="$t('candidateTabs.experience.responsibility')" v-model="newJob.Responsibility" outlined></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.start')} *`" type="date" v-model="newJob.Date_start" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="$t('candidateTabs.experience.finish')" type="date" v-model="newJob.Date_finish" outlined></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.add") }}</v-btn>
                  <v-btn variant="text" @click="closeAddJobDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editJobDialog" max-width="600px">
      <v-card>
        <v-card-title>{{ $t("candidateTabs.experience.editTitle") }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="updateJob">
            <v-container>
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.jobTitle')} *`" v-model="editJob.Job_title" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.company')} *`" v-model="editJob.Company" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea :label="$t('candidateTabs.experience.responsibility')" v-model="editJob.Responsibility" outlined></v-textarea>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="`${$t('candidateTabs.experience.start')} *`" type="date" v-model="editJob.Date_start" :rules="[rules.required]" outlined required></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field :label="$t('candidateTabs.experience.finish')" type="date" v-model="editJob.Date_finish" outlined></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-btn color="primary" type="submit">{{ $t("app.actions.save") }}</v-btn>
                  <v-btn variant="text" @click="closeEditJobDialog">{{ $t("app.actions.cancel") }}</v-btn>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

// Professional experience (jobs) CRUD tab extracted from CandidateDetails.vue.
export default defineComponent({
  name: "CandidateExperienceTab",
  props: {
    candidateId: { type: [String, Number], required: true },
    items: { type: Array, default: () => [] },
  },
  emits: ["refresh", "notify"],
  data() {
    return {
      addJobDialog: false,
      newJob: { Job_title: "", Company: "", Responsibility: "", Date_start: "", Date_finish: "" },
      editJobDialog: false,
      editJob: { id: null, Job_title: "", Company: "", Responsibility: "", Date_start: "", Date_finish: "" },
    };
  },
  computed: {
    rules() {
      return { required: (value) => !!value || this.$t("candidateTabs.common.requiredField") };
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      return dateString.split("T")[0];
    },
    openAddJobDialog() {
      this.newJob = { Job_title: "", Company: "", Responsibility: "", Date_start: "", Date_finish: "" };
      this.addJobDialog = true;
    },
    closeAddJobDialog() {
      this.addJobDialog = false;
    },
    addJob() {
      if (!this.newJob.Job_title.trim() || !this.newJob.Company.trim() || !this.newJob.Date_start.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const payload = {
        id_foreign: this.candidateId,
        Job_title: this.newJob.Job_title,
        Company: this.newJob.Company,
        Responsibility: this.newJob.Responsibility || null,
        Date_start: this.newJob.Date_start,
        Date_finish: this.newJob.Date_finish || null,
      };
      axios
        .post("/candidati/jobs", payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.experience.added"), color: "success" });
          this.addJobDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la adăugarea experienței:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.experience.addError"), color: "error" });
        });
    },
    openEditJobDialog(job) {
      this.editJob = { ...job };
      this.editJobDialog = true;
    },
    closeEditJobDialog() {
      this.editJobDialog = false;
    },
    updateJob() {
      if (!this.editJob.Job_title.trim() || !this.editJob.Company.trim() || !this.editJob.Date_start.trim()) {
        this.$emit("notify", { message: this.$t("candidateTabs.common.requiredEmpty"), color: "error" });
        return;
      }
      const jobId = this.editJob.id;
      const payload = {
        id_foreign: this.candidateId,
        Job_title: this.editJob.Job_title,
        Company: this.editJob.Company,
        Responsibility: this.editJob.Responsibility || null,
        Date_start: this.editJob.Date_start,
        Date_finish: this.editJob.Date_finish || null,
      };
      axios
        .put(`/candidati/jobs/${jobId}`, payload)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.experience.updated"), color: "success" });
          this.editJobDialog = false;
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la actualizarea experienței:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.experience.updateError"), color: "error" });
        });
    },
    confirmDeleteJob(id) {
      if (confirm(this.$t("candidateTabs.experience.deleteConfirm"))) {
        this.deleteJob(id);
      }
    },
    deleteJob(id) {
      axios
        .delete(`/candidati/jobs/${id}`)
        .then(() => {
          this.$emit("notify", { message: this.$t("candidateTabs.experience.deleted"), color: "success" });
          this.$emit("refresh");
        })
        .catch((error) => {
          console.error("Eroare la ștergerea experienței:", error);
          this.$emit("notify", { message: this.$t("candidateTabs.experience.deleteError"), color: "error" });
        });
    },
  },
});
</script>
